import {
	IDrawerDataListColumn,
	PaginatedDrawerAutoDataList,
	laravelPaginatedListFetchConfig,
	useHasPermission,
	useLaravelCrudEntity,
} from "@dgs/core";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { targetGroupService as sharedTargetGroupService } from "../../../shared/api/targetGroupService";
import { PermissionName } from "../../../shared/types/permission";
import { targetGroupService } from "../shared/api/targetGroupService";
import { TargetGroupIndexResource } from "../shared/types";
import { TargetGroupListContextMenu } from "../targetGroups/TargetGroupListContextMenu";
import { TargetGroupCreateDrawer, TargetGroupUpdateDrawer } from "../targetGroups/TargetGroupsDrawer";

export const TargetGroupList: FC = () => {
	const { t } = useTranslation();

	const crudOperations = useLaravelCrudEntity(
		targetGroupService,
		{
			entityName: t("Target group"),
			messages: {
				create: t("Target group was saved."),
				update: t("Target group was saved."),
				delete: t("Target group was deleted."),
			},
		},
		[],
		[sharedTargetGroupService.quickFilterKey],
	);

	const dataListColumns: IDrawerDataListColumn<TargetGroupIndexResource>[] = useMemo(
		() => [
			{
				heading: t("Name"),
				valueKey: "name",
				type: "value",
			},
			{
				heading: "",
				type: "actions",
				size: "max-content",
				action: ({ id }, openDrawer) => (
					<TargetGroupListContextMenu
						targetGroupId={id}
						handleEdit={openDrawer}
						handleDelete={() => crudOperations.deleteEntity(id)}
					/>
				),
			},
		],
		[crudOperations, t],
	);

	const canCreate = useHasPermission<PermissionName>(["targetGroup.create"]);
	const canUpdate = useHasPermission<PermissionName>(["targetGroup.update"]);

	return (
		<PaginatedDrawerAutoDataList
			canUpdate={canUpdate}
			canCreate={canCreate}
			initialSearch={{
				search: "",
				searchFields: [{ name: "name", label: t("Name") }],
			}}
			translations={{
				title: t("Target groups"),
				empty: t("No target groups available"),
			}}
			renderCreateDrawer={(drawerProps) => (
				<TargetGroupCreateDrawer
					{...drawerProps}
					onSubmit={(entityFormState) => crudOperations.createEntity(entityFormState)}
				/>
			)}
			renderUpdateDrawer={(drawerProps) => (
				<TargetGroupUpdateDrawer
					{...drawerProps}
					onSubmit={(entityFormState) => crudOperations.updateEntity(drawerProps.id, entityFormState)}
				/>
			)}
			columns={dataListColumns}
			listFetchConfig={laravelPaginatedListFetchConfig(targetGroupService.listKey, targetGroupService.index)}
		/>
	);
};
